import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_ORIGIN } from '../../config';

/** Whether push is configured server-side, plus the VAPID public key. */
export interface PushConfig {
  enabled: boolean;
  publicKey: string | null;
}

/** The flattened browser subscription the API stores on `devices`. */
export interface PushSubscriptionInput {
  endpoint: string;
  p256dh: string;
  auth: string;
}

/** One line in the header bell's list, in the same words a push would use. */
export interface InboxItem {
  id: string;
  title: string;
  body: string;
  /** Same-origin deep link, e.g. `/#/c/<relationshipId>`. */
  url: string;
  at: string;
  read: boolean;
}

export interface Inbox {
  items: InboxItem[];
  unread: number;
}

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_ORIGIN}/api`, credentials: 'include' }),
  tagTypes: ['PushConfig', 'Inbox'],
  endpoints: (b) => ({
    /** Public: a VAPID public key is not a secret, and the browser needs it. */
    pushConfig: b.query<PushConfig, void>({
      query: () => '/notifications/config',
      providesTags: ['PushConfig'],
    }),
    /** Recent notifications for the header bell, newest first. */
    inbox: b.query<Inbox, void>({
      query: () => '/notifications/inbox',
      providesTags: ['Inbox'],
    }),
    /** The bell list was opened: everything in it has been seen. */
    markInboxRead: b.mutation<void, void>({
      query: () => ({ url: '/notifications/inbox/read', method: 'POST' }),
    }),
    subscribe: b.mutation<void, PushSubscriptionInput>({
      query: (body) => ({ url: '/notifications/subscribe', method: 'POST', body }),
    }),
    unsubscribe: b.mutation<void, { endpoint: string }>({
      query: (body) => ({ url: '/notifications/unsubscribe', method: 'POST', body }),
    }),
    /**
     * "A draft exists for this conversation." Safe to call on every save — the
     * server resets the reminder clock, so only an abandoned draft reminds.
     */
    saveDraftReminder: b.mutation<void, { relationshipId: string }>({
      query: (body) => ({ url: '/notifications/drafts', method: 'POST', body }),
    }),
    /** The draft was sealed or discarded — drop any pending reminder. */
    cancelDraftReminder: b.mutation<void, { relationshipId: string }>({
      query: ({ relationshipId }) => ({
        url: `/notifications/drafts/${relationshipId}`,
        method: 'DELETE',
      }),
    }),
    /** Sign-out: no local draft survives, so cancel every pending reminder. */
    cancelAllDraftReminders: b.mutation<void, void>({
      query: () => ({ url: '/notifications/drafts', method: 'DELETE' }),
    }),
  }),
});

export const {
  usePushConfigQuery,
  useInboxQuery,
  useMarkInboxReadMutation,
  useSubscribeMutation,
  useUnsubscribeMutation,
  useSaveDraftReminderMutation,
  useCancelDraftReminderMutation,
  useCancelAllDraftRemindersMutation,
} = notificationsApi;
